import type {
  AttendanceRecord,
  DailyRecord,
  HomeroomHomeworkRecord,
  HomeroomHomeworkTaskType,
  Student,
  StudentRoutineItem,
} from "@classroom/domain";
import { i18n } from "../i18n";

export type HistoryKind = "attendance" | "missingHomework" | "materials" | "answers" | "routine";
export type HistoryField =
  | "date"
  | "number"
  | "name"
  | "status"
  | "subject"
  | "taskType"
  | "detail"
  | "answers"
  | "title"
  | "note";
export type HistorySheet = { name: string; rows: string[][]; suggestedHeader: number; suggestedKind: HistoryKind };
export type HistoryMap = Partial<Record<HistoryField, number>>;
export type HistoryRow = {
  rowNumber: number;
  studentId?: string;
  studentLabel: string;
  date?: string;
  value: string;
  status: "ready" | "duplicate" | "conflict" | "invalid";
  reason: string;
  attendance?: "present" | "late" | "absent";
  subject?: string;
  taskType?: HomeroomHomeworkTaskType;
  detail?: string;
  answers?: number;
  title?: string;
  note?: string;
};
export type HistorySource = {
  attendanceRecords: AttendanceRecord[];
  homeroomHomeworkRecords: HomeroomHomeworkRecord[];
  dailyRecords: DailyRecord[];
  studentRoutineItems: StudentRoutineItem[];
};
export type HistoryPlan = {
  attendanceRecords: AttendanceRecord[];
  homeroomHomeworkRecords: HomeroomHomeworkRecord[];
  dailyRecords: DailyRecord[];
  studentRoutineItems: StudentRoutineItem[];
};

export const HISTORY_KINDS: Array<{ value: HistoryKind; label: string }> = [
  {
    value: "attendance",
    get label() {
      return i18n.t("marketing:attendanceTab");
    },
  },
  {
    value: "missingHomework",
    get label() {
      return i18n.t("guidedTour:steps.demoHomeroomHomework.title");
    },
  },
  {
    value: "materials",
    get label() {
      return i18n.t("adminRecords:kinds.materials");
    },
  },
  {
    value: "answers",
    get label() {
      return i18n.t("landing:typeAnswers");
    },
  },
  {
    value: "routine",
    get label() {
      return i18n.t("cloudMigration:collections.studentRoutineItems");
    },
  },
];
export const HISTORY_FIELDS: Record<HistoryKind, Array<{ value: HistoryField; label: string; required?: boolean }>> = {
  attendance: [
    {
      value: "date",
      get label() {
        return i18n.t("classroomPerformance:students.date");
      },
      required: true,
    },
    {
      value: "number",
      get label() {
        return i18n.t("rosterAdmin:number");
      },
    },
    {
      value: "name",
      get label() {
        return i18n.t("drawHistory:name");
      },
    },
    {
      value: "status",
      get label() {
        return i18n.t("interfaceExtras:attendanceStatus");
      },
      required: true,
    },
  ],
  missingHomework: [
    {
      value: "date",
      get label() {
        return i18n.t("classroomPerformance:students.date");
      },
      required: true,
    },
    {
      value: "number",
      get label() {
        return i18n.t("rosterAdmin:number");
      },
    },
    {
      value: "name",
      get label() {
        return i18n.t("drawHistory:name");
      },
    },
    {
      value: "subject",
      get label() {
        return i18n.t("interfaceExtras:subject");
      },
    },
    {
      value: "taskType",
      get label() {
        return i18n.t("screenCopy:teacherConsole.type");
      },
    },
    {
      value: "detail",
      get label() {
        return i18n.t("screenCopy:teacherConsole.taskDescription");
      },
      required: true,
    },
  ],
  materials: [
    {
      value: "date",
      get label() {
        return i18n.t("classroomPerformance:students.date");
      },
      required: true,
    },
    {
      value: "number",
      get label() {
        return i18n.t("rosterAdmin:number");
      },
    },
    {
      value: "name",
      get label() {
        return i18n.t("drawHistory:name");
      },
    },
    {
      value: "status",
      get label() {
        return i18n.t("interfaceExtras:materialsStatus");
      },
    },
    {
      value: "note",
      get label() {
        return i18n.t("landing:typeNotes");
      },
    },
  ],
  answers: [
    {
      value: "date",
      get label() {
        return i18n.t("classroomPerformance:students.date");
      },
      required: true,
    },
    {
      value: "number",
      get label() {
        return i18n.t("rosterAdmin:number");
      },
    },
    {
      value: "name",
      get label() {
        return i18n.t("drawHistory:name");
      },
    },
    {
      value: "answers",
      get label() {
        return i18n.t("reportLabels:answerCount");
      },
      required: true,
    },
    {
      value: "note",
      get label() {
        return i18n.t("landing:typeNotes");
      },
    },
  ],
  routine: [
    {
      value: "number",
      get label() {
        return i18n.t("rosterAdmin:number");
      },
    },
    {
      value: "name",
      get label() {
        return i18n.t("drawHistory:name");
      },
    },
    {
      value: "title",
      get label() {
        return i18n.t("reportLabels:exceptionTask");
      },
      required: true,
    },
  ],
};

const aliases: Record<HistoryField, string[]> = {
  date: ["日期", "上課日期", "記錄日期", "點名日期", "date", "lesson date"],
  number: ["班號", "學號", "座號", "學生編號", "student no", "student number", "class no", "roll no", "no"],
  name: ["姓名", "學生姓名", "學生", "name", "student name"],
  status: [
    "狀態",
    "出勤",
    "出勤狀態",
    "點名狀態",
    "欠帶",
    "欠帶狀態",
    "欠帶書簿",
    "欠帶書簿狀態",
    "missing books and notes",
    "missing books and notes status",
    "status",
    "attendance",
  ],
  subject: ["科目", "學科", "subject"],
  taskType: ["類型", "功課類型", "種類", "type", "category"],
  detail: ["項目內容", "功課內容", "功課", "作業", "欠交項目", "內容", "事項", "task", "detail", "homework"],
  answers: ["回答次數", "答題次數", "回答", "次數", "answers", "answer count"],
  title: ["例外事項", "日常事項", "事項名稱", "項目名稱", "title", "item"],
  note: ["備註", "註", "remarks", "notes", "note"],
};
const normalized = (value: string) =>
  value
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase()
    .replace(/[\s_：:()（）.。-]/g, "");
const asDate = (year: number, month: number, day: number) => {
  if (year < 2000 || year > 2100) return undefined;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    : undefined;
};
export function parseHistoryDate(raw: string, date1904 = false) {
  const value = raw.trim();
  if (/^\d{5}(?:\.\d+)?$/.test(value)) {
    const serial = Number(value);
    const date = new Date(
      Date.UTC(date1904 ? 1904 : 1899, date1904 ? 0 : 11, date1904 ? 1 : 30) + Math.floor(serial) * 86400000,
    );
    return asDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }
  const iso = value.match(/^(20\d{2})[-/年.](\d{1,2})[-/月.](\d{1,2})日?$/);
  if (iso) return asDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const hk = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|20\d{2})$/);
  if (hk) return asDate(Number(hk[3]) < 100 ? 2000 + Number(hk[3]) : Number(hk[3]), Number(hk[2]), Number(hk[1]));
  return undefined;
}

function cellText(value: unknown): string {
  if (value == null) return "";
  if (value instanceof Date)
    return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, "0")}-${String(value.getUTCDate()).padStart(2, "0")}`;
  if (["string", "number", "boolean"].includes(typeof value)) return String(value).trim();
  if (typeof value !== "object") return "";
  const item = value as { text?: unknown; result?: unknown; richText?: Array<{ text?: unknown }> };
  if (typeof item.text === "string") return item.text.trim();
  if (Array.isArray(item.richText))
    return item.richText
      .map((part) => String(part.text || ""))
      .join("")
      .trim();
  return item.result == null ? "" : cellText(item.result);
}

export type HistoryCsvEncoding = "utf-8" | "big5";
export type HistoryImportErrorCode = "fileType" | "size" | "empty" | "quotes" | "columns" | "rows" | "encoding";
export class HistoryImportError extends Error {
  constructor(public readonly code: HistoryImportErrorCode) {
    super(code);
    this.name = "HistoryImportError";
  }
}

function tableDelimiter(text: string) {
  const separators = ["\t", ",", ";"];
  const counts = separators.map(() => [0]);
  let row = 0;
  let quoted = false;
  // Sample logical rows: separators and line breaks inside quoted cells are data.
  for (let index = 0; index < Math.min(text.length, 65536) && row < 15; index++) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') index++;
      else quoted = !quoted;
    } else if (!quoted) {
      const column = separators.indexOf(char);
      if (column >= 0) counts[column][row]++;
      else if (char === "\r" || char === "\n") {
        if (char === "\r" && text[index + 1] === "\n") index++;
        row++;
        for (const values of counts) values[row] = 0;
      }
    }
  }
  const scores = counts.map((values) => {
    const frequencies = new Map<number, number>();
    for (const count of values) if (count > 0) frequencies.set(count, (frequencies.get(count) || 0) + 1);
    return Math.max(0, ...[...frequencies].map(([count, frequency]) => frequency * 100 + Math.min(count, 80)));
  });
  const best = Math.max(...scores);
  return best ? separators[scores.indexOf(best)] : ",";
}

export function readHistoryText(text: string, name: string): { sheets: HistorySheet[]; date1904: boolean } {
  if (new TextEncoder().encode(text).byteLength > 10 * 1024 * 1024) throw new HistoryImportError("size");
  const input = text.replace(/^\uFEFF/, "");
  const delimiter = tableDelimiter(input);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  let closedQuote = false;
  function appendCell() {
    row.push(cell.trim());
    if (row.length > 80) throw new HistoryImportError("columns");
    cell = "";
    closedQuote = false;
  }
  function appendRow() {
    appendCell();
    rows.push(row);
    if (rows.length > 5002) throw new HistoryImportError("rows");
    row = [];
  }
  for (let index = 0; index < input.length; index++) {
    const char = input[index];
    if (quoted) {
      if (char !== '"') cell += char;
      else if (input[index + 1] === '"') {
        cell += '"';
        index++;
      } else {
        quoted = false;
        closedQuote = true;
      }
    } else if (char === delimiter) appendCell();
    else if (char === "\r" || char === "\n") {
      appendRow();
      if (char === "\r" && input[index + 1] === "\n") index++;
    } else if (closedQuote) {
      if (!/\s/.test(char)) throw new HistoryImportError("quotes");
    } else if (char === '"') {
      if (cell.trim()) throw new HistoryImportError("quotes");
      cell = "";
      quoted = true;
    } else cell += char;
  }
  if (quoted) throw new HistoryImportError("quotes");
  if (row.length || cell || closedQuote) appendRow();
  if (!rows.some((values) => values.some(Boolean))) throw new HistoryImportError("empty");
  const suggestedHeader = guessHeaderRow(rows);
  return {
    sheets: [{ name, rows, suggestedHeader, suggestedKind: guessKind(name, rows[suggestedHeader] || []) }],
    date1904: false,
  };
}

export async function readHistoryCsv(file: File, encoding: HistoryCsvEncoding = "utf-8") {
  if (!/\.csv$/i.test(file.name)) throw new HistoryImportError("fileType");
  if (file.size > 10 * 1024 * 1024) throw new HistoryImportError("size");
  const buffer = await file.arrayBuffer();
  let text: string;
  try {
    text = new TextDecoder(encoding, { fatal: true }).decode(buffer);
  } catch {
    throw new HistoryImportError("encoding");
  }
  return readHistoryText(text, file.name);
}

export async function readHistoryWorkbook(file: File): Promise<{ sheets: HistorySheet[]; date1904: boolean }> {
  if (!/\.xlsx$/i.test(file.name)) throw new Error("請選擇 .xlsx Excel 檔案。");
  if (file.size > 10 * 1024 * 1024) throw new Error("檔案不可大於 10 MB。");
  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load((await file.arrayBuffer()) as never);
  const sheets = workbook.worksheets
    .map((sheet) => {
      const rows: string[][] = [];
      if (sheet.rowCount > 5002 || sheet.columnCount > 80)
        throw new Error(`「${sheet.name}」太大，請分拆為每頁最多 5,000 筆、80 欄。`);
      for (let i = 1; i <= sheet.rowCount; i++)
        rows.push(
          Array.from({ length: Math.min(sheet.columnCount, 80) }, (_, index) =>
            cellText(sheet.getRow(i).getCell(index + 1).value),
          ),
        );
      const suggestedHeader = guessHeaderRow(rows);
      return {
        name: sheet.name,
        rows,
        suggestedHeader,
        suggestedKind: guessKind(sheet.name, rows[suggestedHeader] || []),
      };
    })
    .filter((sheet) => sheet.rows.some((row) => row.some(Boolean)));
  if (!sheets.length) throw new Error("Excel 沒有可讀取的工作表。");
  return { sheets, date1904: Boolean(workbook.properties.date1904) };
}

export function guessHeaderRow(rows: string[][]) {
  let best = 0;
  let bestScore = -1;
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const score = rows[i].reduce(
      (count, cell) =>
        count +
        (Object.values(aliases).some((values) => values.some((alias) => normalized(alias) === normalized(cell)))
          ? 1
          : 0),
      0,
    );
    if (score > bestScore) {
      best = i;
      bestScore = score;
    }
  }
  return best;
}
export function guessKind(name: string, headers: string[]): HistoryKind {
  const text = normalized(`${name} ${headers.join(" ")}`);
  if (/日常事項|例外事項|不訂餐|byod/.test(text)) return "routine";
  if (/欠交|missinghomework/.test(text)) return "missingHomework";
  if (/回答次數|答題次數|課堂回應|answers/.test(text)) return "answers";
  if (/欠帶|missingmaterials|missingbooksandnotes/.test(text)) return "materials";
  return "attendance";
}
export function guessMap(headers: string[], kind: HistoryKind): HistoryMap {
  const result: HistoryMap = {};
  for (const field of HISTORY_FIELDS[kind]) {
    const index = headers.findIndex((header) =>
      aliases[field.value].some((alias) => normalized(header) === normalized(alias)),
    );
    if (index >= 0) result[field.value] = index;
  }
  return result;
}

function taskType(raw: string): HomeroomHomeworkTaskType {
  const value = normalized(raw);
  if (/回條|通告|replyslip/.test(value)) return "replySlip";
  if (/筆記|notes/.test(value)) return "notes";
  if (/工作紙|worksheet/.test(value)) return "worksheet";
  if (/堂課|課堂|classwork/.test(value)) return "classwork";
  if (/溫習|默書|revision/.test(value)) return "revision";
  if (/作業|練習|exercise/.test(value)) return "exercise";
  return "other";
}
function attendanceStatus(raw: string): HistoryRow["attendance"] {
  const value = normalized(raw);
  if (["出席", "到", "正常", "present", "p", "有到"].includes(value)) return "present";
  if (["遲到", "late", "l"].includes(value)) return "late";
  if (["缺席", "缺課", "病假", "事假", "absent", "a"].includes(value)) return "absent";
  return undefined;
}
function sameHomework(item: HomeroomHomeworkRecord, row: HistoryRow) {
  return (
    item.date === row.date &&
    normalized(item.subject) === normalized(row.subject || "") &&
    (item.taskType || "other") === row.taskType &&
    normalized(item.taskDetail || "") === normalized(row.detail || "")
  );
}
function existingStatus(
  kind: HistoryKind,
  row: HistoryRow,
  source: HistorySource,
  classId: string,
): { status: HistoryRow["status"]; reason: string } {
  if (!row.studentId) return { status: "invalid", reason: row.reason };
  const studentId = row.studentId;
  if (kind === "attendance") {
    const record = source.attendanceRecords.find((item) => item.classId === classId && item.date === row.date);
    if (record?.studentIds.includes(studentId)) {
      const current: "present" | "late" | "absent" = Object.hasOwn(record.exceptions, studentId)
        ? record.exceptions[studentId]
        : "present";
      return current === row.attendance
        ? { status: "duplicate", reason: "系統已有相同點名記錄" }
        : {
            status: "conflict",
            reason: `系統現有狀態：${current === "present" ? "出席" : current === "late" ? "遲到" : "缺席"}`,
          };
    }
  }
  if (
    kind === "missingHomework" &&
    source.homeroomHomeworkRecords.some(
      (item) => item.classId === classId && sameHomework(item, row) && item.missingStudentIds.includes(studentId),
    )
  )
    return { status: "duplicate", reason: "系統已有相同欠交記錄" };
  if (
    kind === "routine" &&
    source.studentRoutineItems.some(
      (item) =>
        item.classId === classId &&
        normalized(item.title) === normalized(row.title || "") &&
        item.studentIds.includes(studentId),
    )
  )
    return { status: "duplicate", reason: "系統已有相同日常事項" };
  if (kind === "materials" || kind === "answers") {
    const current = source.dailyRecords.find((item) => item.classId === classId && item.date === row.date)?.students[
      studentId
    ];
    if (kind === "materials" && current?.missingMaterials)
      return { status: "duplicate", reason: "系統已有相同欠帶書簿記錄" };
    if (kind === "answers" && current?.answers)
      return current.answers === row.answers
        ? { status: "duplicate", reason: "系統已有相同回答次數" }
        : { status: "conflict", reason: `系統現有回答次數：${current.answers}` };
  }
  return { status: "ready", reason: "可匯入" };
}

export function reviewHistorySheet(
  sheet: HistorySheet,
  kind: HistoryKind,
  headerRow: number,
  map: HistoryMap,
  students: Student[],
  source: HistorySource,
  classId: string,
  date1904 = false,
): HistoryRow[] {
  const required = HISTORY_FIELDS[kind].filter((field) => field.required && map[field.value] == null);
  if (required.length || (map.number == null && map.name == null))
    throw new Error(
      `請指定${[...required.map((field) => field.label), ...(map.number == null && map.name == null ? ["班號或姓名"] : [])].join("、")}欄。`,
    );
  const byNumber = new Map<number, Student[]>();
  for (const student of students) byNumber.set(student.number, [...(byNumber.get(student.number) ?? []), student]);
  const byName = new Map<string, Student[]>();
  for (const student of students)
    byName.set(normalized(student.name), [...(byName.get(normalized(student.name)) || []), student]);
  const seen = new Map<string, HistoryRow>();
  const result: HistoryRow[] = [];
  for (let index = headerRow + 1; index < sheet.rows.length; index++) {
    const cells = sheet.rows[index];
    if (!cells.some(Boolean)) continue;
    const get = (field: HistoryField) => (map[field] == null ? "" : (cells[map[field]!] || "").trim());
    const number = get("number");
    const name = get("name");
    const matches = name ? byName.get(normalized(name)) || [] : [];
    const numbered = /^\d+$/.test(number) ? (byNumber.get(Number(number)) ?? []) : [];
    const candidates =
      numbered.length > 1 && name
        ? numbered.filter((student) => normalized(student.name) === normalized(name))
        : numbered;
    const student = number
      ? /^\d+$/.test(number)
        ? candidates.length === 1
          ? candidates[0]
          : undefined
        : undefined
      : matches.length === 1
        ? matches[0]
        : undefined;
    const row: HistoryRow = {
      rowNumber: index + 1,
      studentId: student?.id,
      studentLabel: [number && `${number} 號`, name].filter(Boolean).join(" ") || "未填學生",
      value: "",
      status: "invalid",
      reason: "",
    };
    if (!student)
      row.reason = number
        ? candidates.length > 1
          ? `班號 ${number} 對應多位學生，請加上姓名以辨識`
          : `找不到班號 ${number}`
        : name
          ? matches.length > 1
            ? "同名學生多於一位，請加上班號"
            : `找不到學生「${name}」`
          : "未填班號或姓名";
    else if (name && normalized(student.name) !== normalized(name))
      row.reason = `班號和姓名不一致：${student.number} 號是 ${student.name}`;
    else if (kind !== "routine") {
      row.date = parseHistoryDate(get("date"), date1904);
      if (!row.date) row.reason = `日期無法辨認：${get("date") || "空白"}`;
    }
    if (kind === "attendance") {
      row.attendance = attendanceStatus(get("status"));
      row.value = get("status");
      if (!row.attendance && !row.reason) row.reason = `出勤狀態無法辨認：${row.value || "空白"}`;
    }
    if (kind === "missingHomework") {
      row.subject = get("subject");
      row.taskType = taskType(get("taskType"));
      row.detail = get("detail");
      row.value = [row.subject, get("taskType"), row.detail].filter(Boolean).join("｜");
      if (!row.detail && !row.reason) row.reason = "項目內容空白";
    }
    if (kind === "materials") {
      row.note = get("note");
      row.value = get("status") || i18n.t("classroomPerformance:students.missingMaterials");
      if (/^(否|沒有|無|否定|0|no|false|正常)$/i.test(normalized(row.value))) row.reason = "此行沒有欠帶書簿，毋須匯入";
    }
    if (kind === "answers") {
      row.answers = Number(get("answers"));
      row.note = get("note");
      row.value = get("answers");
      if (!Number.isInteger(row.answers) || row.answers < 0 || row.answers > 1000)
        row.reason = "回答次數須為 0 至 1000 的整數";
      else if (row.answers === 0) row.reason = "回答次數為 0，毋須匯入";
    }
    if (kind === "routine") {
      row.title = get("title");
      row.value = row.title;
      if (!row.title && !row.reason) row.reason = "例外事項空白";
    }
    if (!row.reason) {
      const existing = existingStatus(kind, row, source, classId);
      row.status = existing.status;
      row.reason = existing.reason;
      const key = [
        kind,
        row.date,
        row.studentId,
        kind === "missingHomework"
          ? `${normalized(row.subject || "")}|${row.taskType}|${normalized(row.detail || "")}`
          : kind === "routine"
            ? normalized(row.title || "")
            : "",
      ].join("|");
      const first = seen.get(key);
      if (first) {
        row.status = first.value === row.value ? "duplicate" : "conflict";
        row.reason = `${first.rowNumber} 行已有${first.value === row.value ? "相同" : "不同"}資料`;
      } else seen.set(key, row);
    }
    result.push(row);
  }
  return result;
}

export function buildHistoryPlan(
  kind: HistoryKind,
  selected: HistoryRow[],
  source: HistorySource,
  classId: string,
  workspaceId: string,
): HistoryPlan {
  const now = new Date().toISOString();
  const plan: HistoryPlan = {
    attendanceRecords: [],
    homeroomHomeworkRecords: [],
    dailyRecords: [],
    studentRoutineItems: [],
  };
  const attendance = new Map<string, AttendanceRecord>();
  const homework = new Map<string, HomeroomHomeworkRecord>();
  const daily = new Map<string, DailyRecord>();
  const routine = new Map<string, StudentRoutineItem>();
  for (const row of selected) {
    if (!row.studentId || row.status === "invalid" || row.status === "duplicate") continue;
    if (kind === "attendance") {
      let record = attendance.get(row.date!);
      if (!record) {
        const existing = source.attendanceRecords.find((item) => item.classId === classId && item.date === row.date);
        record = existing
          ? {
              ...existing,
              studentIds: [...existing.studentIds],
              exceptions: { ...existing.exceptions },
              updatedAt: now,
            }
          : {
              id: `${classId}_${row.date}`,
              workspaceId,
              classId,
              date: row.date!,
              studentIds: [],
              exceptions: {},
              updatedAt: now,
            };
        attendance.set(row.date!, record);
      }
      if (!record.studentIds.includes(row.studentId)) record.studentIds.push(row.studentId);
      if (row.attendance === "present") delete record.exceptions[row.studentId];
      else if (row.attendance) record.exceptions[row.studentId] = row.attendance;
    } else if (kind === "missingHomework") {
      const key = [row.date, normalized(row.subject || ""), row.taskType, normalized(row.detail || "")].join("|");
      let record = homework.get(key);
      if (!record) {
        const existing = source.homeroomHomeworkRecords.find(
          (item) => item.classId === classId && sameHomework(item, row),
        );
        record = existing
          ? { ...existing, missingStudentIds: [...existing.missingStudentIds], updatedAt: now }
          : {
              id: `history_${crypto.randomUUID()}`,
              workspaceId,
              classId,
              date: row.date!,
              subject: row.subject || "",
              taskType: row.taskType,
              taskDetail: row.detail,
              missingStudentIds: [],
              createdAt: now,
              updatedAt: now,
            };
        homework.set(key, record);
      }
      if (!record.missingStudentIds.includes(row.studentId)) record.missingStudentIds.push(row.studentId);
    } else if (kind === "routine") {
      const key = normalized(row.title || "");
      let record = routine.get(key);
      if (!record) {
        const existing = source.studentRoutineItems.find(
          (item) => item.classId === classId && normalized(item.title) === key,
        );
        record = existing
          ? { ...existing, studentIds: [...existing.studentIds], updatedAt: now }
          : {
              id: `routine_${crypto.randomUUID()}`,
              workspaceId,
              classId,
              title: row.title!,
              studentIds: [],
              createdAt: now,
              updatedAt: now,
            };
        routine.set(key, record);
      }
      if (!record.studentIds.includes(row.studentId)) record.studentIds.push(row.studentId);
    } else {
      let record = daily.get(row.date!);
      if (!record) {
        const existing = source.dailyRecords.find((item) => item.classId === classId && item.date === row.date);
        record = existing
          ? { ...existing, students: { ...existing.students }, updatedAt: now }
          : { id: `${classId}_${row.date}`, workspaceId, classId, date: row.date!, students: {}, updatedAt: now };
        daily.set(row.date!, record);
      }
      const previous = record.students[row.studentId] || { missingMaterials: false, answers: 0 };
      record.students[row.studentId] =
        kind === "materials"
          ? { ...previous, missingMaterials: true, note: row.note || previous.note }
          : { ...previous, answers: row.answers || 0, note: row.note || previous.note };
    }
  }
  plan.attendanceRecords = [...attendance.values()];
  plan.homeroomHomeworkRecords = [...homework.values()];
  plan.dailyRecords = [...daily.values()];
  plan.studentRoutineItems = [...routine.values()];
  return plan;
}
